import i18n from "@/i18n";
import store from "@/store";

import type { Nullish } from "@/models/common";
import type { IExperianBopConsumerScoreModels } from "@/models/options";
import type {
  DataServiceContextValue,
  IBopConsumerScoreModelLevel,
  IRelativeDateRange,
  ISpecificDateRange,
  RelativeLookbackRange,
  SpecificYear,
} from "@/models/orchestration";

abstract class BaseContextToString<
  TContextValueType = DataServiceContextValue,
> {
  contextValue: TContextValueType;

  constructor(contextValue: DataServiceContextValue) {
    this.contextValue = contextValue as TContextValueType;
  }

  abstract get matches(): boolean;
  abstract toString(): string;
}

class BopConsumerScoreModelLevelToString extends BaseContextToString<IBopConsumerScoreModelLevel> {
  get matches(): boolean {
    return !!this.contextValue.level;
  }

  toString() {
    const bopConsumerScoreModels =
      store.state.options.all?.experian_bop_consumer_score_models ??
      ({} as IExperianBopConsumerScoreModels);

    return bopConsumerScoreModels[this.contextValue.level] ?? "";
  }
}

class RelativeDateRangeToString extends BaseContextToString<IRelativeDateRange> {
  get matches() {
    return (
      !!this.contextValue.include_month_to_date ||
      !!this.contextValue.lookback_months
    );
  }

  toString() {
    const t = i18n.global.t;
    const { lookback_months, include_month_to_date } = this.contextValue;

    const monthToDateLabel =
      include_month_to_date && t("ORCHESTRATION.MONTH_TO_DATE");

    const lastMonthsLabel =
      lookback_months &&
      t("ORCHESTRATION.LAST_NUMBER_MONTHS", { months: lookback_months });
    return [monthToDateLabel, lastMonthsLabel].filter(Boolean).join(" ");
  }
}

class SpecificDateRangeToString extends BaseContextToString<ISpecificDateRange> {
  get matches() {
    return ["from", "through"].every((property) =>
      Object.prototype.hasOwnProperty.call(this.contextValue, property)
    );
  }

  toString() {
    const t = i18n.global.t;
    const { from, through } = this.contextValue;
    return `${t("COMMON.DATES.PERIOD")}: ${from.slice(0, 7)} - ${through.slice(
      0,
      7
    )}`;
  }
}

class SpecificYearToString extends BaseContextToString<SpecificYear> {
  get matches() {
    return !!this.contextValue.year;
  }

  toString() {
    const t = i18n.global.t;
    const { year } = this.contextValue;
    return `${t("COMMON.YEAR")}: ${year}`;
  }
}

class RelativeLookbackRangeToString extends BaseContextToString<RelativeLookbackRange> {
  get matches() {
    return ["lookback_months_from", "lookback_months_through"].every(
      (property) =>
        Object.prototype.hasOwnProperty.call(this.contextValue, property)
    );
  }
  /**
   * Examples:
   * "Period: Month-to-date"
   * "Period: Last month"
   * "Period: Last 3 months"
   * "Period: 2024-01 - 2024-03"
   * "Period: Prior 3 - 6 months"
   */
  toString() {
    const t = i18n.global.t;
    const { lookback_months_from, lookback_months_through } = this.contextValue;
    const isRanged = lookback_months_from !== lookback_months_through;
    let text = `${t("COMMON.DATES.PERIOD")}: `;

    const monthSuffix = ` ${t(
      "COMMON.DATES.DATEPICKER_TYPE.MONTH",
      lookback_months_from
    )}`.toLowerCase();

    const toFormatted =
      lookback_months_through === 0
        ? t("ORCHESTRATION.MONTH_TO_DATE")
        : lookback_months_through;

    if (isRanged) {
      if (typeof toFormatted === "number" && toFormatted > 1) {
        text += `${t("COMMON.DATES.PRIOR")} `;
      }
      text += `${toFormatted} - ${lookback_months_from}${monthSuffix}`;
    } else {
      if (typeof toFormatted === "string") {
        text += `${toFormatted}`;
      } else {
        const numberOfMonths =
          lookback_months_from < 2 ? "" : ` ${lookback_months_from}`;
        text += `${t("COMMON.LAST")}${numberOfMonths}${monthSuffix}`;
      }
    }

    return text;
  }
}

const contextParsers: {
  new (
    contextValue: DataServiceContextValue
  ): InstanceType<typeof BaseContextToString>;
}[] = [
  BopConsumerScoreModelLevelToString,
  RelativeDateRangeToString,
  RelativeLookbackRangeToString,
  SpecificDateRangeToString,
  SpecificYearToString,
];

/**
 * Accepts a data service context and returns a human readable representation of it
 * @param value Data service context.value property
 * @returns The string representation of the context value
 */
export default function contextToString(
  value: DataServiceContextValue | Nullish
) {
  let stringValue = "";

  if (value) {
    for (const Parser of contextParsers) {
      const parser = new Parser(value);
      if (parser.matches) {
        stringValue = parser.toString();
        break;
      }
    }
  }
  return stringValue;
}
